import { useState, useEffect, useContext, useCallback, useRef, Suspense } from "react";
import { Chip } from "@material-ui/core";
import { DynamicForm } from "../../../../../@types/dynamicForm.types";
import { AuthContext, UIContext } from "../../../../../contexts";
import { ADMIN_DASHBOARD_HEADER } from "../../../../../constants/caption/dashboardHeader";
import DynamicReportEditableForm from "../../../../shared/dynamicEditableForm/DynamicEditableForm";
import { api } from "../../../../../utils/api";
import { Validation } from "../../../../../@types/validation.types";
import { trackPromise } from "react-promise-tracker";
import SnackbarAPICall from "../../../../shared/snackbar/SnackbarAPICall";
import { SnackbarProps } from "../../../../../@types/snackbarAPI.types";
import { Member } from "../../../../../@types/member.types";
import JSONToExcel from "../../../../../utils/commonFunctions/JSONToExcel";
import { monthEndDateGenerator, monthStartDateGenerator } from "../../../../../utils/commonFunctions/date";
import { Plan } from "../../../../../@types/plan.types";

import useRouteParam from "../../../../../hooks/useRouteParam";
import { Group } from "../../../../../@types/group.types";
import { useEmployerRoute } from "../../../../../hooks";
import { MONTH_LIST } from "../../../../../constants/monthList";
import { VEB_BILLING_REPORT_MONTH, VEB_BILLING_YEAR_LIST } from "../../../../../constants/enrollmentYearList";
import { VEBCarriers } from "../../../../../constants/planNames";

const chipMessage = "This report will pull all the billing details of VEB Plans.";

const VEBBillingReport = (): JSX.Element => {
	const [reportDetailsDynamicForm, setReportDetailsDynamicForm] = useState<DynamicForm>();
	const { setDashboardHeader } = useContext(UIContext);
	const [previousMonthList, setPreviousMonthList] = useState<string[]>([]);
	const isEmployerRoute = useEmployerRoute();
	const queryGroupNumber = useRouteParam("gn");
	const [selectedGroup, setSelectedGroup] = useState<Group>();
	const [reportFieldDetails, setReportFieldDetails] = useState<any>({
		alternative_report_name: "",
		group_number: null,
		carrier_name: "",
		billing_year: "",
		billing_month: "",
		start_date: "",
		end_date: ""
	});
	const { user } = useContext(AuthContext); // Extracting logged in user from central storage.
	const [assignedGroups, setAssignedGroups] = useState([]);
	const [validation, setValidation] = useState<Validation>({
		reportFieldDetails: {},
		status: "invalid"
	});
	const _validation = useRef<Validation>();
	const [snackbarAPICallProps, setSnackbarAPICallProps] = useState<SnackbarProps>({
		open: false,
		severity: undefined,
		message: "",
		handleSnackbarClose: (event, reason) => {
			if (reason === "clickaway") return;
			setSnackbarAPICallProps(
				Object.assign({}, snackbarAPICallProps, {
					open: false
				})
			);
		}
	});

	const handleValidation = useCallback(async () => {
		const { group_number, carrier_name, billing_year, billing_month } = reportFieldDetails;

		_validation.current = {
			...validation,
			reportFieldDetails: {},
			status: "invalid"
		};
		let flag = true;

		// employee validation checking
		if (group_number === null) {
			_validation.current.reportFieldDetails["group_number"] = "Group is required";
			_validation.current["status"] = "invalid";
			flag = false;
		}
		// if (start_date.length === 0) {
		// 	_validation.current.reportFieldDetails["start_date"] = "Start date is required";
		// 	_validation.current["status"] = "invalid";
		// 	flag = false;
		// }
		// if (end_date.length === 0) {
		// 	_validation.current.reportFieldDetails["end_date"] = "End date is required";
		// 	_validation.current["status"] = "invalid";
		// 	flag = false;
		// }
		// if (start_date.length !== 0) {
		// 	if (compareInvalidDates(start_date, end_date)) {
		// 		_validation.current.reportFieldDetails["start_date"] = "Start Date cannot be greater than End Date!";
		// 		_validation.current["status"] = "invalid";
		// 		flag = false;
		// 	}
		// }
		// if (end_date.length !== 0) {
		// 	if (compareInvalidDates(start_date, end_date)) {
		// 		_validation.current.reportFieldDetails["end_date"] = "End Date cannot be less than Start Date!";
		// 		_validation.current["status"] = "invalid";
		// 		flag = false;
		// 	}
		// }

		if (carrier_name.length === 0) {
			_validation.current.reportFieldDetails["carrier_name"] = "Carrier Name is required";
			_validation.current["status"] = "invalid";
			flag = false;
		}

		if (billing_year.length === 0) {
			_validation.current.reportFieldDetails["billing_year"] = "Enrollment Year is required";
			_validation.current["status"] = "invalid";
			flag = false;
		}
		if (billing_month.length === 0) {
			_validation.current.reportFieldDetails["billing_month"] = "Benefit Month is required";
			_validation.current["status"] = "invalid";
			flag = false;
		}

		if (flag === true) _validation.current["status"] = "valid";
		else {
			_validation.current["status"] = "invalid";
			alert("Please, fill all required fields!");
		}
		setValidation(Object.assign({}, _validation.current));
		return _validation.current["status"];
	}, [reportFieldDetails, validation]);

	const getAssignGroups = useCallback(async () => {
		const params = {
			user_id: user?._id
		};
		const response = await api.assignGroupsAndLocation.getAssignedGroupsAndLocations(params);

		if (response) {
			const _assignedGroups: any[] = [];
			response.groupsAndLocations.forEach((group: any) => {
				if (group.assigned_group_info) {
					_assignedGroups.push(group);
				}
			});

			setAssignedGroups(Object.assign([], _assignedGroups));
		}
	}, [user?._id]);

	const handleSelectGroup = useCallback(
		(queryGroupNumber: number) => {
			// // console.log("Query Group Number", queryGroupNumber);

			assignedGroups.forEach((assignedGroup: Group) => {
				if (assignedGroup.group_number === queryGroupNumber) {
					setSelectedGroup(Object.assign({}, assignedGroup));
					setReportFieldDetails(
						Object.assign({}, reportFieldDetails, {
							group_number: queryGroupNumber
						})
					);
				}
			});
		},

		// eslint-disable-next-line react-hooks/exhaustive-deps
		[assignedGroups]
	);

	const handleChange = useCallback(
		(event: React.ChangeEvent<HTMLInputElement>) => {
			const { name, value } = event.target;

			if (name === "carrier_name") {
				setReportFieldDetails(
					Object.assign({}, reportFieldDetails, {
						[name]: value
					})
				);
			} else if (name === "billing_month") {
				if (reportFieldDetails.billing_year) {
					setReportFieldDetails(
						Object.assign({}, reportFieldDetails, {
							[name]: value,
							start_date: monthStartDateGenerator(value, Number(reportFieldDetails.billing_year)),
							end_date: monthEndDateGenerator(value, Number(reportFieldDetails.billing_year))
						})
					);
				} else {
					alert("Please select the Enrollment Year first!");
				}
			} else if (name === "billing_year") {
				if (reportFieldDetails.billing_month) {
					setReportFieldDetails(
						Object.assign({}, reportFieldDetails, {
							[name]: value,
							start_date: monthStartDateGenerator(reportFieldDetails.billing_month, Number(value)),
							end_date: monthEndDateGenerator(reportFieldDetails.billing_month, Number(value))
						})
					);
				} else {
					setReportFieldDetails(Object.assign({}, reportFieldDetails, { [name]: value }));
				}
			} else setReportFieldDetails(Object.assign({}, reportFieldDetails, { [name]: value }));
		},
		[reportFieldDetails]
	);

	const handleGenerateReportSubmit = useCallback(
		async (event: React.MouseEvent<HTMLButtonElement>) => {
			try {
				event.preventDefault();
				const validation = await handleValidation();
				if (validation === "valid") {
					// console.log("VEB Billing Report Generation");
					const { group_number, carrier_name, billing_month, billing_year, start_date, end_date } =
						reportFieldDetails;

					const VEBBillingPayload = {
						group_number,
						carrier_name,
						billing_year,
						billing_month,
						start_date,
						end_date
					};

					// console.log("VEBBillingPayload", VEBBillingPayload);
					const response: any[] = await trackPromise(api.reports.VEBBillingReport(VEBBillingPayload));

					if (response) {
						// console.log("Response VEBBillingPayload", response);

						const fileName = reportFieldDetails.alternative_report_name
							? reportFieldDetails.alternative_report_name
							: "VEB Billing Report";

						const excelPayload = {
							user: {
								role: user?.role,
								name: user?.first_name + " " + user?.last_name
							},
							reportFieldDetails,
							response
						};

						await trackPromise(JSONToExcel(excelPayload, fileName));

						setTimeout(() => {
							setSnackbarAPICallProps(
								Object.assign({}, snackbarAPICallProps, {
									open: true,
									message: "VEB Billing Report Fetched Successfully and File Downloaded!",
									severity: "success"
								})
							);
						}, 2000);
					}
				}
			} catch (error) {
				alert("VEB Billing Report Generation Unsuccessful!");
				throw error;
			}
		},
		[handleValidation, reportFieldDetails, user, snackbarAPICallProps]
	);

	useEffect(() => {
		setDashboardHeader(ADMIN_DASHBOARD_HEADER.veb_billing_report);
		getAssignGroups();
	}, [getAssignGroups, setDashboardHeader]);

	useEffect(() => {
		if (isEmployerRoute && queryGroupNumber) handleSelectGroup(queryGroupNumber as number);
	}, [isEmployerRoute, queryGroupNumber, handleSelectGroup]);

	useEffect(() => {
		if (reportFieldDetails.start_date === null)
			setReportFieldDetails(Object.assign({}, reportFieldDetails, { start_date: "" }));
		if (reportFieldDetails.end_date === null)
			setReportFieldDetails(Object.assign({}, reportFieldDetails, { end_date: "" }));
	}, [reportFieldDetails]);

	useEffect(() => {
		let previousMonth = new Date().getMonth() - 1;
		if (previousMonth < 0) {
			previousMonth = 11;
		}
		const _previousMonthList: string[] = [];
		for (let idx = 0; idx <= previousMonth; idx++) {
			_previousMonthList.push(MONTH_LIST[idx]);
		}
		setPreviousMonthList(Object.assign([], _previousMonthList));
	}, []);

	useEffect(() => {
		setReportDetailsDynamicForm(
			Object.assign(
				{},
				{
					"VEB Billing Report Generation": [
						{
							label: "Group",
							name: "group_number",
							onChange: (event: React.ChangeEvent<HTMLInputElement>) => handleChange(event),
							value:
								isEmployerRoute && queryGroupNumber
									? selectedGroup?.name
									: reportFieldDetails.group_number,
							type: isEmployerRoute && queryGroupNumber ? "textfield" : "select",
							options: [...assignedGroups]
						},
						{
							label: "VEB Carrier Name",
							name: "carrier_name",
							onChange: (event: React.ChangeEvent<HTMLInputElement>) => handleChange(event),
							value: reportFieldDetails.carrier_name,
							type: "select",
							options: ["All", ...VEBCarriers]
						},
						{
							label: "Billing Year",
							name: "billing_year",
							onChange: (event: React.ChangeEvent<HTMLInputElement>) => handleChange(event),
							value: reportFieldDetails.billing_year,
							type: "select",
							options: VEB_BILLING_YEAR_LIST
						},
						{
							label: "Billing Month",
							name: "billing_month",
							onChange: (event: React.ChangeEvent<HTMLInputElement>) => handleChange(event),
							value: reportFieldDetails.billing_month,
							type: "select",
							// options: previousMonthList
							options: VEB_BILLING_REPORT_MONTH
						},
						{
							label: "Alternative Report Name",
							name: "alternative_report_name",
							onChange: (event: React.ChangeEvent<HTMLInputElement>) => handleChange(event),
							placeholder: "Enter alternative report name",
							value: reportFieldDetails.alternative_report_name,
							type: "textfield"
						}
					]
				}
			)
		);
	}, [assignedGroups, reportFieldDetails, handleChange, isEmployerRoute, queryGroupNumber, selectedGroup?.name]);

	// console.log("reportFieldDetails", reportFieldDetails);

	return (
		<div className="census-report-container" id="census-report-container">
			<Suspense fallback={<div />}>
				<SnackbarAPICall snackbarProps={snackbarAPICallProps} />
			</Suspense>
			<div className="chip-container" id="chip-container">
				<Chip className="chip-green" label={chipMessage} variant="outlined" />
			</div>
			<DynamicReportEditableForm
				reportDetailsDynamicForm={reportDetailsDynamicForm}
				// selectedPlanName={selectedPlan?.plan_name}
				handleClearDateField={() => null}
				validation={validation}
				handleGenerateReportSubmit={handleGenerateReportSubmit}
			/>
		</div>
	);
};

export default VEBBillingReport;
